import { type QueryClient, type QueryObserverResult, useQueries, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useRef } from "react";
import { type AnalyticValues, useAnalytics } from "../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { getStaffingDate } from "../utils/dateUtils";
import type { DemoOriginsContext } from "../utils/demoRequestOriginSeed";
import { demoLedgerOf, demoOriginsSignature } from "../utils/demoRequestOrigins";
import { errorText } from "../utils/errors";
import { isLegacyDemoDepartmentId, isRealDemoDepartmentId, type RequestLedger, RequestOriginsStatus, type ShiftRequest } from "../utils/models";
import { hasUnknown } from "../utils/requestOriginsLedger";
import { useDemoOriginsContext } from "../utils/useDemoOriginsContext";
import { useFeatureFlag } from "./useOpenFeatureFlag";

export type RequestOriginsResult = {
	status: RequestOriginsStatus;
	byRequestId: ReadonlyMap<string, RequestLedger>;
	/** Loads again whatever failed. Does nothing unless `status` is `Failed`. */
	retry: () => void;
};

export const REQUEST_ORIGINS_QUERY_KEY_ROOT = "request-origins";
/** `MAX_ORIGINS_WINDOW_DAYS` and `MAX_ORIGINS_PAGE_SIZE` in `backend/models/request_origins.py`. */
export const MAX_ORIGINS_WINDOW_DAYS = 31;
export const ORIGINS_PAGE_SIZE = 200;

const NO_RETRY = (): void => {};
const UNAVAILABLE: RequestOriginsResult = { status: RequestOriginsStatus.Unavailable, byRequestId: new Map(), retry: NO_RETRY };

type TrackFn = (event: string, props?: AnalyticValues) => void;

/** One `GET /requests/origins` window: schedule days `[startDate, endDate]`, its requests, and what they look like now. */
export type OriginWindow = { startDate: string; endDate: string; requestIds: ReadonlySet<string>; signature: string };

/** What a request's ledger ends at: a change to its count or status changes the answer, so it refetches. */
const requestSignature = (request: ShiftRequest): string =>
	JSON.stringify([request.id, request.status, "needed_nurses" in request ? request.needed_nurses : null]);

const isoDate = (day: DateTime): string => {
	const iso = day.toISODate();
	if (iso === null) throw new Error(`Invalid schedule day ${day.invalidExplanation}`);
	return iso;
};

/**
 * The fewest windows of at most 31 schedule days that hold every request, each keyed by its requests' ids, counts and
 * statuses. Days come from the department's clock and day start, with the scheduled grace — the same rule the API uses
 * to pick a window's requests — so every request lands in the window that asks for it.
 */
export const originWindowsOf = (requests: readonly ShiftRequest[], timezone: string, dayStart: string, graceMinutes: number): OriginWindow[] => {
	const byDay = new Map<string, ShiftRequest[]>();
	for (const request of requests) {
		const day = isoDate(getStaffingDate(request.message.shift_start.setZone(timezone), dayStart, graceMinutes));
		const onDay = byDay.get(day);
		if (onDay === undefined) byDay.set(day, [request]);
		else onDay.push(request);
	}
	const windows: OriginWindow[] = [];
	type Open = { start: DateTime; endDate: string; requests: ShiftRequest[] };
	let current: Open | null = null;
	const close = (window: Open): void => {
		windows.push({
			startDate: isoDate(window.start),
			endDate: window.endDate,
			requestIds: new Set(window.requests.map((request) => request.id)),
			signature: window.requests.map(requestSignature).sort().join("|"),
		});
	};
	for (const [day, onDay] of [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b))) {
		const date = DateTime.fromISO(day, { zone: timezone });
		if (current !== null && date.diff(current.start, "days").days < MAX_ORIGINS_WINDOW_DAYS) {
			current.endDate = day;
			current.requests.push(...onDay);
			continue;
		}
		if (current !== null) close(current);
		current = { start: date, endDate: day, requests: [...onDay] };
	}
	if (current !== null) close(current);
	return windows;
};

/** Every page of one window, in order. Pages chain on `next_offset`, so they are asked for one after another. */
const fetchPages = async (departmentId: string, window: OriginWindow, offset: number): Promise<RequestLedger[]> => {
	const { data } = await backendService.getRequestOrigins(departmentId, window.startDate, window.endDate, ORIGINS_PAGE_SIZE, offset);
	if (data.next_offset === null) return data.items;
	return [...data.items, ...(await fetchPages(departmentId, window, data.next_offset))];
};

const fetchWindow = async (departmentId: string, window: OriginWindow, track: TrackFn, trackError: TrackFn): Promise<RequestLedger[]> => {
	const props = { department_id: departmentId, start_date: window.startDate, end_date: window.endDate };
	track("request_origins_load_start", props);
	try {
		const ledgers = await fetchPages(departmentId, window, 0);
		track("request_origins_load_success", { ...props, ledger_count: ledgers.length, unknown_count: ledgers.filter(hasUnknown).length });
		return ledgers;
	} catch (error) {
		trackError("request_origins_load_error", { ...props, error_message: errorText(error) });
		throw error;
	}
};

/**
 * Each of the window's requests' newest ledger from any earlier answer in the department. A changed count or status, or
 * a request that moves the window's first or last day, is a new key — a new observer, so TanStack's `keepPreviousData`
 * has nothing to keep — and without this every card in the window would lose its glyph and the filter would fall back
 * to its skeleton until the refetch lands.
 */
const lastAnswerOf = (queryClient: QueryClient, departmentKey: readonly unknown[], requestIds: ReadonlySet<string>): RequestLedger[] | undefined => {
	const answered = queryClient
		.getQueryCache()
		.findAll({ queryKey: departmentKey })
		.filter((query) => query.state.data !== undefined)
		.sort((a, b) => a.state.dataUpdatedAt - b.state.dataUpdatedAt);
	const newest = new Map<string, RequestLedger>();
	for (const query of answered) {
		// The cache holds whatever was stored under this key root, which only `fetchWindow` writes.
		for (const ledger of query.state.data as RequestLedger[]) {
			if (requestIds.has(ledger.request_id)) newest.set(ledger.request_id, ledger);
		}
	}
	// None of these requests has ever been answered (a range not loaded yet): nothing to show, and the caller reads the
	// window as Loading, so the filter shows its skeleton rather than hiding a whole range it knows nothing about.
	if (newest.size === 0) return undefined;
	return [...newest.values()];
};

const combineWindows = (results: QueryObserverResult<RequestLedger[]>[]): RequestOriginsResult => {
	const byRequestId = new Map<string, RequestLedger>();
	for (const result of results) {
		// Still loading: nothing to add yet.
		if (result.data === undefined) continue;
		for (const ledger of result.data) {
			// A request that never had a slot open (one created already done) has nothing to explain, so it gets no glyph.
			if (ledger.entries.length > 0) byRequestId.set(ledger.request_id, ledger);
		}
	}
	const failed = results.filter((result) => result.isError);
	if (failed.length > 0) {
		return { status: RequestOriginsStatus.Failed, byRequestId, retry: () => failed.forEach((result) => void result.refetch()) };
	}
	const status = results.some((result) => result.data === undefined) ? RequestOriginsStatus.Loading : RequestOriginsStatus.Ready;
	return { status, byRequestId, retry: NO_RETRY };
};

/** The real ledgers, from the API: one query per window, keyed so a change to any request's id, count or status refetches it. */
const useApiOrigins = (enabled: boolean, departmentId: string, requests: readonly ShiftRequest[]): RequestOriginsResult => {
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const { chosenDepartment, timezone } = useHospitalData();
	const { dayStart, scheduled_shift_delta_minutes: graceMinutes } = chosenDepartment;
	// A refresh can add history and end at the same count, so the list's successful refresh must reload its ledger.
	const openShiftsUpdatedAt = queryClient.getQueryState(["openShifts", departmentId])?.dataUpdatedAt;
	const windows = useMemo(
		() =>
			enabled
				? originWindowsOf(
						// After a department switch the list can still hold the previous department's requests for a moment.
						requests.filter((request) => request.department_id === departmentId),
						timezone,
						dayStart,
						graceMinutes,
					)
				: [],
		[enabled, requests, departmentId, timezone, dayStart, graceMinutes],
	);
	return useQueries({
		queries: windows.map((window) => {
			const departmentKey = [REQUEST_ORIGINS_QUERY_KEY_ROOT, departmentId];
			return {
				queryKey: [...departmentKey, window.startDate, window.endDate, window.signature, openShiftsUpdatedAt],
				queryFn: () => fetchWindow(departmentId, window, track, trackError),
				// No staleTime: the history keeps growing even when a count returns to an earlier value (2 → 3 → 2), so a key
				// seen before is refetched when it comes back rather than served from its old answer.
				// DEFAULT: a count change refetches its window and its cards keep their last ledgers meanwhile, reported Ready so
				// the glyphs and the filter don't flicker. Until it lands a changed ledger can lag the count, and a request new to
				// the window has none, so an active Origin filter leaves it out for that moment.
				placeholderData: () => lastAnswerOf(queryClient, departmentKey, window.requestIds),
			};
		}),
		combine: combineWindows,
	});
};

type CachedLedger = { signature: string; ledger: RequestLedger };

/**
 * The demo mock: seeded from the loaded roster, keyed to the seeding context — a roster or clock change re-seeds
 * everything, a request refresh alone does not, so an unchanged request keeps the same ledger object.
 */
const useDemoOrigins = (enabled: boolean, requests: readonly ShiftRequest[]): RequestOriginsResult => {
	const demoContext = useDemoOriginsContext(enabled);
	const cacheRef = useRef<{ context: DemoOriginsContext | null; byRequestId: Map<string, CachedLedger> }>({ context: null, byRequestId: new Map() });
	return useMemo<RequestOriginsResult>(() => {
		if (demoContext === null) return UNAVAILABLE;
		if (cacheRef.current.context !== demoContext) cacheRef.current = { context: demoContext, byRequestId: new Map() };
		const cache = cacheRef.current.byRequestId;
		const byRequestId = new Map<string, RequestLedger>();
		for (const request of requests) {
			const signature = demoOriginsSignature(request);
			const cached = cache.get(request.id);
			const ledger = cached !== undefined && cached.signature === signature ? cached.ledger : demoLedgerOf(request, demoContext);
			cache.set(request.id, { signature, ledger });
			byRequestId.set(request.id, ledger);
		}
		return { status: RequestOriginsStatus.Ready, byRequestId, retry: NO_RETRY };
	}, [requests, demoContext]);
};

enum OriginsSource {
	None = "none",
	Demo = "demo",
	Api = "api",
}

const sourceOf = (departmentId: string, loadedDepartmentId: string): OriginsSource => {
	// Windows and the demo's roster come from the loaded department, so a caller asking about another one gets nothing.
	if (departmentId !== loadedDepartmentId) return OriginsSource.None;
	// The legacy demos serve fake requests from S3, with no history to read.
	if (isLegacyDemoDepartmentId(departmentId)) return OriginsSource.None;
	return isRealDemoDepartmentId(departmentId) ? OriginsSource.Demo : OriginsSource.Api;
};

/**
 * Why each request's slots opened and closed, keyed by request id. Real departments read the API in windows of at most
 * 31 schedule days; `inhouse\d*__` demo departments keep the seeded mock (the API answers them 404); the legacy
 * `InHouse__` demos get `Unavailable`. Call it once per surface and pass the map down.
 */
export const useRequestOrigins = (departmentId: string, requests: readonly ShiftRequest[]): RequestOriginsResult => {
	const { chosenDepartment } = useHospitalData();
	const originsEnabled = useFeatureFlag(FEATURE_FLAGS.RequestOrigins);
	const source = originsEnabled ? sourceOf(departmentId, chosenDepartment.department_id) : OriginsSource.None;
	const demo = useDemoOrigins(source === OriginsSource.Demo, requests);
	const api = useApiOrigins(source === OriginsSource.Api, departmentId, requests);
	switch (source) {
		case OriginsSource.Demo:
			return demo;
		case OriginsSource.Api:
			return api;
		case OriginsSource.None:
			return UNAVAILABLE;
		default: {
			const unhandled: never = source;
			throw new Error(`Unhandled origins source ${String(unhandled)}`);
		}
	}
};
